import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  // Login function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Google Sign-In function
  function googleSignIn() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  // Logout function
  function logout() {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user_mobile');
    localStorage.removeItem('user_name');
    setCurrentUser(null); // Force state update immediately
    return signOut(auth);
  }

  useEffect(() => {
    // onAuthStateChanged returns a specific unsubscribe function for cleanup
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Force refresh token to get latest claims
          const tokenResult = await user.getIdTokenResult(true);
          const userIsAdmin = !!tokenResult.claims.admin;

          // You might want to store this in state
          user.isAdmin = userIsAdmin;

          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser({ ...user, ...userData, isAdmin: userIsAdmin });
          } else {
            setCurrentUser({ ...user, isAdmin: userIsAdmin });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setCurrentUser(user);
        }
      } else {
        // Fallback for Custom OTP Login (stored in localStorage)
        const localToken = localStorage.getItem('jwt_token');
        const localMobile = localStorage.getItem('user_mobile');
        const localName = localStorage.getItem('user_name');

        if (localToken && localMobile) {
          setCurrentUser({
            displayName: localName || 'User',
            phoneNumber: localMobile,
            uid: 'custom-otp-user', // Placeholder UID
            isAdmin: false // Assuming custom OTP users aren't admins by default via this method
          });
        } else {
          setCurrentUser(null);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Derived state for backward compatibility if needed, though checking currentUser is better
  const isLoggedIn = !!currentUser;
  const isAdmin = currentUser?.isAdmin || false;

  const value = {
    currentUser,
    isLoggedIn,
    isAdmin,
    signup,
    login,
    googleSignIn,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);